// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
/**
 * Shared admin toast (README "Toast/Notification Patterns").
 *
 * `useToast()` keeps one auto-dismissing toast per page; `<Toast />` renders it
 * fixed top-right with the semantic success / error / info styling. Used by the
 * Automations and Messages tabs so both behave identically.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ErrorIcon, InfoIcon, SuccessIcon, XIcon } from "../../design-system";
import { typography } from "../../design-system/tokens";

export const TOAST_MS = 4500;

const toastStyles = {
  success: {
    box: "bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800",
    text: "text-green-800 dark:text-green-300",
    icon: <SuccessIcon size="md" color="success" aria-hidden="true" />,
  },
  error: {
    box: "bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800",
    text: "text-red-800 dark:text-red-300",
    icon: <ErrorIcon size="md" color="danger" aria-hidden="true" />,
  },
  info: {
    box: "bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800",
    text: "text-blue-800 dark:text-blue-300",
    icon: <InfoIcon size="md" color="primary" aria-hidden="true" />,
  },
};

/** `{ toast, show(type, message), dismiss() }` — `show` replaces the current toast and restarts the timer. */
export const useToast = () => {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setToast(null);
  }, []);
  const show = useCallback((type, message) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ id: Date.now(), type, message });
    timerRef.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);
  return { toast, show, dismiss };
};

export const Toast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const style = toastStyles[toast.type] || toastStyles.info;
  return (
    <div
      className={`fixed top-4 right-4 z-[9500] max-w-sm flex items-start gap-3 px-4 py-3 rounded-2xl shadow-lg border ${style.box}`}
      role="status"
      aria-live="polite"
    >
      <span className="shrink-0 mt-0.5">{style.icon}</span>
      <span className={`${typography.bodySmall} ${style.text} break-words`}>{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className={`ml-auto shrink-0 p-0.5 rounded ${style.text} opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
        aria-label="Dismiss notification"
      >
        <XIcon size="sm" />
      </button>
    </div>
  );
};

Toast.propTypes = {
  toast: PropTypes.shape({ id: PropTypes.number, type: PropTypes.string, message: PropTypes.string }),
  onDismiss: PropTypes.func.isRequired,
};

export default Toast;
